import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    productNameRequired: { type: String, default: "Product name is required" },
    variantPrefix: { type: String, default: "Variant %{number}" },
    salePricePositive: { type: String, default: "Sale price must be a positive number" },
    fieldRequired: { type: String, default: "%{field} is required" },
    fixErrors: { type: String, default: "Please fix the following errors:" },
    tabHasErrors: { type: String, default: "This variant has errors" },
    fieldFallback: { type: String, default: "Field" }
  }

  connect() {
    this.boundValidate = this.validate.bind(this)
    this.element.addEventListener('submit', this.boundValidate)

    // Revalidate on blur to clear fixed errors
    this.element.addEventListener('blur', this.revalidate.bind(this), true)
  }

  disconnect() {
    this.element.removeEventListener('submit', this.boundValidate)
  }

  revalidate() {
    // Only revalidate if there are currently errors shown
    if (!this.element.querySelector('[data-error-summary]')) return

    this.clearErrors()
    const errors = this.collectErrors()
    if (errors.length > 0) {
      this.showErrors(errors, { scroll: false, switchTab: false })
    }
  }

  validate(event) {
    // Clear previous error states
    this.clearErrors()

    const errors = this.collectErrors()

    if (errors.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      this.showErrors(errors)
      return false
    }

    return true
  }

  collectErrors() {
    const errors = []

    // Validate product name
    const nameField = this.element.querySelector('input[name="product[name]"]')
    if (nameField && !nameField.value.trim()) {
      errors.push({
        field: nameField,
        message: this.productNameRequiredValue,
        section: 'product'
      })
    }

    // Get all variant panels and find visible ones
    const allPanels = Array.from(this.element.querySelectorAll('.variant-fields'))

    // Filter to only non-removed panels
    const activePanels = allPanels.filter(panel => {
      const destroyInput = panel.querySelector('input[name*="_destroy"]')
      if (destroyInput && destroyInput.value === '1') return false
      if (panel.dataset.removed === 'true') return false
      return true
    })

    activePanels.forEach((panel, visibleIndex) => {
      const variantNumber = visibleIndex + 1

      // Check sale price
      const salePriceField = panel.querySelector('input[name*="[sale_price]"]')
      if (salePriceField) {
        const value = parseFloat(salePriceField.value)
        if (isNaN(value) || value < 0) {
          const prefix = this.variantPrefixValue.replace('%{number}', variantNumber)
          errors.push({
            field: salePriceField,
            message: `${prefix}: ${this.salePricePositiveValue}`,
            section: 'variant',
            variantIndex: visibleIndex,
            panel: panel
          })
        }
      }

      // Check required variant options (only actual form fields, not Trix internal inputs)
      const requiredFields = panel.querySelectorAll('input[required][name*="product"], select[required][name*="product"], textarea[required][name*="product"]')
      requiredFields.forEach(field => {
        // Skip hidden inputs and Trix-related inputs
        if (field.type === 'hidden') return
        if (field.closest('trix-toolbar') || field.closest('trix-editor')) return
        // Skip sale_price - it has its own validation with translated message
        if (field.name && field.name.includes('[sale_price]')) return

        if (!field.value || !field.value.trim()) {
          const label = this.getFieldLabel(field) || this.fieldFallbackValue
          const prefix = this.variantPrefixValue.replace('%{number}', variantNumber)
          const fieldMsg = this.fieldRequiredValue.replace('%{field}', label)
          errors.push({
            field: field,
            message: `${prefix}: ${fieldMsg}`,
            section: 'variant',
            variantIndex: visibleIndex,
            panel: panel
          })
        }
      })
    })

    return errors
  }

  getFieldLabel(field) {
    // Try to find label by for attribute
    const id = field.id
    if (id) {
      const label = this.element.querySelector(`label[for="${id}"]`)
      if (label) return label.textContent.replace('*', '').trim()
    }

    // Try to find label in parent
    const parentLabel = field.closest('div')?.querySelector('label')
    if (parentLabel) return parentLabel.textContent.replace('*', '').trim()

    // Try field name as last resort
    if (field.name) {
      const match = field.name.match(/\[(\w+)\]$/)
      if (match) {
        const name = match[1].replace(/_/g, ' ')
        return name.charAt(0).toUpperCase() + name.slice(1)
      }
    }

    return null
  }

  clearErrors() {
    // Remove error highlights from fields
    this.element.querySelectorAll('.border-red-500, .ring-red-500').forEach(el => {
      el.classList.remove('border-red-500', 'ring-red-500', 'ring-1')
    })

    // Remove error badges from tabs
    this.element.querySelectorAll('[data-error-badge]').forEach(el => el.remove())

    // Clear stored error indexes
    this.errorVariantIndexes = []

    // Remove error summary
    const existingSummary = this.element.querySelector('[data-error-summary]')
    if (existingSummary) existingSummary.remove()
  }

  showErrors(errors, options = {}) {
    const { scroll = true, switchTab = true } = options

    // Create error summary
    const summary = document.createElement('div')
    summary.setAttribute('data-error-summary', 'true')
    summary.className = 'bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4'
    summary.innerHTML = `
      <div class="flex items-start gap-3">
        <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
        <div>
          <h3 class="text-sm font-medium text-red-800 dark:text-red-200">${this.fixErrorsValue}</h3>
          <ul class="mt-2 list-disc list-inside text-sm text-red-700 dark:text-red-300 space-y-1">
            ${errors.map(e => {
              if (e.variantIndex !== undefined) {
                return `<li><button type="button" class="text-left hover:underline" data-error-goto="${e.variantIndex}">${e.message}</button></li>`
              }
              return `<li>${e.message}</li>`
            }).join('')}
          </ul>
        </div>
      </div>
    `

    // Insert at the very beginning of the form
    this.element.prepend(summary)

    // Make "go to" links clickable to switch tabs
    summary.querySelectorAll('button[data-error-goto]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = btn.dataset.errorGoto
        this.switchToTab(parseInt(idx))
      })
    })

    // Highlight fields with errors
    errors.forEach(error => {
      if (error.field) {
        error.field.classList.add('border-red-500', 'ring-1', 'ring-red-500')
      }
    })

    // Collect error variant indexes
    const variantErrors = errors.filter(e => e.section === 'variant')
    this.errorVariantIndexes = [...new Set(variantErrors.map(e => e.variantIndex))]

    // Switch to first error tab (only on initial validation, not revalidation)
    if (switchTab) {
      const firstVariantError = errors.find(e => e.section === 'variant')
      if (firstVariantError) {
        this.switchToTab(firstVariantError.variantIndex)

        // Focus the error field after tab switch
        setTimeout(() => {
          if (firstVariantError.field) {
            firstVariantError.field.focus()
          }
        }, 150)
      }
    }

    // Add error badges to variant tabs (after tab switch which rebuilds tabs)
    this.errorVariantIndexes.forEach(variantIndex => {
      this.addErrorBadgeToTab(variantIndex)
    })

    // Scroll to top to show error summary (only on initial validation)
    if (scroll) {
      summary.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  addErrorBadgeToTab(variantIndex) {
    const tabsContainer = this.element.querySelector('[data-variant-tabs-target="tabsContainer"]')
    if (!tabsContainer) return

    // Get all tab wrappers (they contain the buttons)
    const tabWrappers = tabsContainer.querySelectorAll('div.flex.items-center')
    const tabWrapper = tabWrappers[variantIndex]

    if (tabWrapper && !tabWrapper.querySelector('[data-error-badge]')) {
      const badge = document.createElement('span')
      badge.setAttribute('data-error-badge', 'true')
      badge.className = 'ml-1 w-2 h-2 bg-red-500 rounded-full inline-block animate-pulse'
      badge.title = this.tabHasErrorsValue
      tabWrapper.appendChild(badge)
    }
  }

  switchToTab(variantIndex) {
    const variantTabsElement = this.element.querySelector('[data-controller*="variant-tabs"]')
    if (!variantTabsElement) return

    const variantTabsController = this.application.getControllerForElementAndIdentifier(
      variantTabsElement,
      'variant-tabs'
    )

    if (variantTabsController && typeof variantTabsController.showTab === 'function') {
      variantTabsController.showTab(variantIndex)

      // Re-add error badges after tab switch (showTab rebuilds tabs HTML)
      if (this.errorVariantIndexes) {
        this.errorVariantIndexes.forEach(idx => {
          this.addErrorBadgeToTab(idx)
        })
      }
    }
  }
}
